import { AppContext } from "App";

import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IResultMessage } from "@multiweb/types/contracts";
import { isVersionOlderOrEqual } from "Global/Base/Utils/versionUtils";
import { IModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { ErrorHandler } from "UI/Errors";
import { ApplicationTheme } from "UI/Shared/ApplicationTheme";
import { IWorkerInitializationMessageData } from "UI/UI.Worker/WorkerMessages";
import { DIContainer } from "../../../Global/Base/DIContainer";
import { Event } from "../../../Global/Base/Events";
import { Deferred, PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { EnumUtils } from "../../../Global/Base/Utils/EnumUtils";
import { OWExceptions } from "../../../OW.Remoting/Basic/OWExceptions";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { WorkerCommunicator, workerSymbol as communicatorWorkerSymbol } from "../../../OW.Remoting/Remoting/WorkerCommunicator";
import { FirmlyDisabledRights } from "../Rights/FirmlyDisabledRights";
import { IContentBuilder } from "./DataBuilders/IContentBuilder";
import { UserMsgsBuilder, UserMsgsData } from "./DataBuilders/UserMsgsBuilder";
import { FormManager } from "./FormManager/IFormManager";
import { ISystemOperationRights } from "./Helpers/SystemOperationRights";
import { MessageWindow } from "./MessageWindow";
import { IMainMenuFactory, IUIManagerWorker, MessageButtonResult, UIHistoryState } from "./UIManager";
import { IMainMenu } from "./UIManagerInterfaces";

const logger = LazyLoadingSectionLogger.create(LoggingSection.coreUI);

// abstraktni trida - je nutne naimplementovat v konkretni projektu
export abstract class UIManagerWorker implements IUIManagerWorker {
    /* Doba po kterou je loader zobrazený po požadavku na skrytí, zamezuje problikávání loaderu */
    public static get loaderHidingDelay(): number {
        return 500;
    }
    /** Časový úsek kdy se loader nezobrazí [ms]*/
    public static get loaderInactiveDelay(): number {
        return 100;
    }
    /** Pokud se loader zobrazí bude zobrazen minimálně tolik ms*/
    public static get loaderMinDelay(): number {
        return 800;
    }
    protected _mainMenu?: IMainMenu;
    private readonly _preloadFormCount: number;

    protected readonly _mainMenuFactory: IMainMenuFactory | null;
    protected readonly _dataEventsManager: IDataEventsManager;

    protected _homePage: HTMLElement;
    protected _resizeObserver: ResizeObserver;

    public windowResized: Event<UIManagerWorker, any> = new Event<UIManagerWorker, any>();

    private _workers: Map<symbol, any> = new Map<symbol, any>();
    private _workerLoaded: Promise<WorkerCommunicator>;

    private _menuOverlayClicked: Event<IUIManagerWorker, MouseEvent>;

    //proměnné pro práci s loaderem
    /** Handler pro časovač, kdy je zobrazen jen overlay bez loaderu (0-100ms) */
    private _handleShowLoader: number;
    /** Handler pro časovač, který zajistí, že pokud je zobrazen loader, je viditelný po minimální dobu a zabraní tak problikávání */
    private _handleLoaderProtection: number;
    /** Handler pro časovač skrytí loader. Při přichozím požadavku na HIDE se spustí tento odpočet. Po jeho vypršení se provede skutečný HIDE. Handler může být zrušen např. voláním SHOW */
    private _handleHideLoader: number;
    /** Deffered promise, který je nastaven při zobrazení loaderu a resolved po vypršení minimální doby zobrazení loaderu */
    private _deffererLoaderMinTimeProtection: Deferred<void>;
    /** Deffered promise, který se použije v situaci kdy je zavoláno HIDE, ale čeká se na _deffererLoaderMinTimeProtection, až loader bude zobrazen po minimální dobu.
     * Ošetřila se tím situace, kdy mezi počkáním na _deffererLoaderMinTimeProtection mohl přijít SHOW a skrytí loaderu už není žádoucí.
     */
    private _deffererHideLoader: Deferred<void>;

    private _applicationTheme: ApplicationTheme | undefined;

    /**Mapa elementů a metod které zpracovávají resize*/
    private _resizeListeners: Map<HTMLElement, (() => void)[]>;

    public resizeObserve(element: HTMLElement, resizeListener: () => void): void {
        if (!this._resizeListeners.has(element)) {
            //Pokud ještě není element sledovaný, vytvoří so nový klíč v mapě a spustí se sledování
            this._resizeListeners.set(element, [resizeListener]);
            this._resizeObserver.observe(element);
        } else {
            //Pokud již element sledovaný je, zaregistruje se nový listener
            const listenersForElement = this._resizeListeners.get(element);
            listenersForElement.push(resizeListener);
        }
    }

    public resizeUnobserve(element: HTMLElement, resizeListener: () => void): void {
        if (this._resizeListeners.has(element)) {
            //Pokud je element mezi registrovanými sledovanými elementy, najdeme příslušný listener pro odregistrování
            const listenersForElement = this._resizeListeners.get(element);
            const index = listenersForElement.findIndex(listener => listener === resizeListener);
            if (index > -1) {
                //zrušíme registraci listeneru
                listenersForElement.splice(index, 1);
                if (listenersForElement.length === 0) {
                    //pokud již nemáme pro tento element jiné listenery, zrušíme sledování elementu a odstraníme klíč z mapy
                    this._resizeListeners.delete(element);
                    this._resizeObserver.unobserve(element);
                }
            }
        }
    }

    public get menuOverlayClicked(): Event<IUIManagerWorker, MouseEvent> {
        if (this._menuOverlayClicked === undefined) {
            this._menuOverlayClicked = new Event<IUIManagerWorker, MouseEvent>();
        }

        return this._menuOverlayClicked;
    }

    public abstract get blockingLayer(): HTMLElement;

    public get applicationTheme(): ApplicationTheme | undefined {
        return this._applicationTheme;
    }

    public getWorkerBySymbol(smbl: symbol): any {
        return this._workers.get(smbl);
    }

    //#region Constructors
    constructor(preloadFormCount: number = 0, mainMenuFactory: IMainMenuFactory | null, dataEventsManager: IDataEventsManager) {
        this._preloadFormCount = preloadFormCount;
        this._mainMenuFactory = mainMenuFactory;
        this._dataEventsManager = dataEventsManager;

        const iframe = document.querySelector("#formsPlace > iframe");
        if (iframe) {
            this._homePage = iframe as HTMLElement;
        }

        window.addEventListener("popstate", (event) => {
            if (event.state && event.state.Type) {
                this.loadUIHistoryState(event.state);
            }
        });

        //Spuštění resize observeru
        const formPlaces = document.getElementById("formsPlace");
        this._resizeListeners = new Map();

        this._resizeObserver = new ResizeObserver((entries) => {
            entries.forEach(entry => {
                /*Pokud se změní velikost elementu formsPlace tak se spustí událost windowResized UIManagera,
                pokud je to jiný element spustí se resizeListener přidružený k tomuto elementu.*/
                if (entry.target === formPlaces) {
                    this.windowResized.trigger(this, entry);
                } else {
                    const resizedElement = entry.target as HTMLElement;
                    if (this._resizeListeners.has(resizedElement)) {
                        for (const listener of this._resizeListeners.get(resizedElement)) {
                            listener();
                        }
                    }
                }
            });
        });

        if (formPlaces) {
            this._resizeObserver.observe(formPlaces);
        }

        this._workerLoaded = WorkerCommunicator.registerWorker("js/TescoSW.Worker.UI.Loader.js",
            async () => {
                if (self === top) {
                    return null;
                }

                if (this._workerLoaded) {
                    await this._workerLoaded;
                }

                return this.getWorkerBySymbol(communicatorWorkerSymbol);
            },
        );
        if (this._workerLoaded) {
            this._workerLoaded.then((w) => {
                const message: {type: "initialize"; data: IWorkerInitializationMessageData } = {
                    type: "initialize",
                    data: {
                        communicationUrl: AppContext.instance.addresses.serviceUrl,
                        useLegacyApi: isVersionOlderOrEqual(AppContext.instance.configuration.getDevelopmentOption("webServiceVersion"), "15.03"),
                        preloadFormCount: this._preloadFormCount,
                        appId: AppContext.instance.appId,
                        loggingSettings: AppContext.instance.configuration.logging,
                        loggingSection: LoggingSection.coreUI,
                    },
                };
                w.sendOneWayMessage(message);
                this._workers.set(communicatorWorkerSymbol, w);
                this._dataEventsManager.subscribeSystemCacheReloaded("Rights", () => {
                    this._invalidateFormDefinitionCache();
                    DIContainer.instance.create(ISystemOperationRights).clearCache();
                    FirmlyDisabledRights.clearCache();
                });
                this._dataEventsManager.subscribeSystemCacheReloaded("Forms", () => {
                    this._invalidateFormDefinitionCache();
                });
                this._dataEventsManager.subscribeSystemCacheReloaded("DataModel", () => {
                    DIContainer.instance.create(IModelDescriptionsManager).clearCache();
                    OWManager.clearEnumCache();
                });
            });
        }
    }
    //#endregion Constructors

    public init(): void {
        // skryté hlavním menu
        if (this._mainMenuFactory === null) {
            return;
        }

        FormManager.instance.initialized.then(() => {
            this._createMainMenu();
        });
    }

    /**
     * Vytvoří objekt hlavního menu. Je možné přepsat ve vlastní implementaci UIManageru pro vytváření vlastního hlavního menu.
     */
    protected _createMainMenu(): void {
        // kontrola na to zda obsahuje element formsMenu, pokud ho neobsauhuje jedna se o okno formulare, ktere nema mit menu
        const mainMenuElement = document.getElementById("formsMenu");
        if (mainMenuElement != null) {
            this._mainMenu = this._mainMenuFactory();
        }
    }

    public loadUIHistoryState(_state: UIHistoryState): void {
        // Prázdná metoda
    }

    private static async _getFormHtmlElement(htmlDef: string): Promise<HTMLElement> {
        const parser = new DOMParser();
        const html = parser.parseFromString(htmlDef, "text/html");

        const result = (html.querySelector("div[data-behaviour=form]") as HTMLElement);
        //#region Úprava kvůli custom element
        // DOMParser, si vytvarel vlastni document, ktery nemel zaregistrovany customElementy a tedy se braly jako HTMLUnknownElement, a nedoslo k jejich správnemu
        // zpracování, ale zaroven ten DOMParser, vytvari nejaky ocesany document, protoze v nem neexistuje registerContext na registraci novych CustomElementu a tedy
        // se to muselo udelat pres innerHTML
        const el = document.createElement("div");

        // kontrola na sessionTimeout
        if (!result) {
            try {
                await OWManager.getUserRoleIDs();
            } catch (err) {
                let errEx: any = null;
                try {
                    errEx = OWExceptions.parseJSON(err);
                } catch (e) {
                    errEx = err;
                }
                ErrorHandler.instance.handleError(errEx);
                throw "SessionTimeout";
            }
        }

        if (result != null) {
            el.innerHTML = result.outerHTML;
            //#endregion
            if (el.querySelector("div[data-behaviour=form]")) {
                return el.querySelector("div[data-behaviour=form]") as HTMLElement;
            }
        }
        return null;
    }

    public async loadFormDefinition(formName: string, previewId?: string): Promise<HTMLElement> {
        await this._workerLoaded;
        const worker = this.getWorkerBySymbol(communicatorWorkerSymbol) as WorkerCommunicator;
        const formDef: string = await worker.sendMessage({
            type: "form",
            data: { formName, previewId: previewId ?? FormManager.instance.previewId },
        });
        return await UIManagerWorker._getFormHtmlElement(formDef);
    }

    /**
     * Zneplatní cache s načtenými definicemi formulářů
     */
    private async _invalidateFormDefinitionCache(): Promise<void> {
        const worker = this.getWorkerBySymbol(communicatorWorkerSymbol) as WorkerCommunicator;
        return await worker.sendMessage({ type: "invalidateCache" });
    }

    public abstract showMessage(mesgWin: MessageWindow): Promise<MessageButtonResult>;

    //-------------- loader -------------------------------------
    // #region Loader
    public abstract showBlockingLayer(content?: IContentBuilder): void;

    public abstract hideBlockingLayer(): void;

    public abstract actualizeLoader(visible: boolean, actualizeSpinner?: boolean): void;

    public abstract actualizeOverlay(visible: boolean, formsPlaceOnly?: boolean): void;

    public abstract showMenuOverlay(): void;

    public abstract hideMenuOverlay(): void;

    private _loaderCounter: number = 0;

    public showOverlay(visible: boolean, formsPlaceOnly?: boolean): void {
        this.actualizeOverlay(visible, formsPlaceOnly);
    }

    public showLoader(): void {
        this._loaderCounter++;
        //pokud HIDE čekal na zobrazení loaderu po minimální dobu, zruší mu callback
        if (this._deffererHideLoader) {
            this._deffererHideLoader.reject();
        }

        //pokud běží timer pro skrytí loaderu, zruší ho
        if (this._handleHideLoader) {
            clearTimeout(this._handleHideLoader);
            this._handleHideLoader = null;
        } else {
            document.documentElement.setAttribute("data-loading", "");
        }

        //neběží časovač pro zobrazení loaderu ani není loader aktuálně zobrazen
        if (!this._handleShowLoader && !this._deffererLoaderMinTimeProtection) {
            this.actualizeLoader(true, false);
            //spustí timer, který po zadanou dobu zobrazuje jen blocking layer, po vypršení se zobrazí loader, který má minimální dobu po kterou musí být zobrazen
            this._handleShowLoader = self.setTimeout(() => {
                this._showLoader();
                this._handleShowLoader = null;
            }, UIManagerWorker.loaderInactiveDelay);
        }
    }

    /** Po vypršení krátké doby, kdy je zobrazen jen overlay zobrazí loader, který bude zobrazen po minimální dobu */
    private _showLoader(): void {
        this._deffererLoaderMinTimeProtection = PromiseUtils.defer<void>();
        if (!this._handleLoaderProtection) {
            this._handleLoaderProtection = self.setTimeout(() => {
                this._deffererLoaderMinTimeProtection.resolve();
                this._deffererLoaderMinTimeProtection = null;
                this._handleLoaderProtection = null;
            }, UIManagerWorker.loaderMinDelay);
        }
        this.actualizeLoader(true);
    }

    public hideLoader(): void {
        if (this._loaderCounter > 0)
            this._loaderCounter--;
        //běží timer doby kdy loader není ještě zobrazen
        if (this._handleShowLoader && this._loaderCounter === 0) {
            clearTimeout(this._handleShowLoader);
            this._handleShowLoader = null;
            this._hideLoader();
            return;
        }

        //spustí timer po jehož vypršení se skryje loader
        if (!this._handleHideLoader) {
            this._handleHideLoader = self.setTimeout(() => {
                this._hideLoader();
                this._handleHideLoader = null;
            }, UIManagerWorker.loaderHidingDelay);
        }
    }

    /** Provede skutečné skrytí loader */
    private _hideLoader(): void {
        //běží timer doby kdy loader není ještě zobrazen
        if (this._handleShowLoader && this._loaderCounter === 0) {
            clearTimeout(this._handleShowLoader);
            this._handleShowLoader = null;
        }
        //běží timer minimální doby po kterou musí být loader zobrazen.
        if (this._deffererLoaderMinTimeProtection) {
            //Počká do uplynutí a pokud SHOW nezruší _deffererHideLoader, provede skrytí.
            this._deffererHideLoader = PromiseUtils.deferStateful<void>();
            this._deffererHideLoader.promise.then(() => this._hideLoader()).catch(() => {
                // není třeba nijak ošetřovat ani propagovat, jedná se o očekávanou situaci, kdy přijde požadavek na zobrazení loaderu ve chvíli, kdy se čeká na minimální dobu pro skrytí
            });
            this._deffererLoaderMinTimeProtection.promise.then(() => {
                if (this._deffererHideLoader)
                    this._deffererHideLoader.resolve();
            });
        } else if (this._loaderCounter === 0) {
            //skryje loader i overlay
            this.actualizeLoader(false);
            document.documentElement.removeAttribute("data-loading");
        }
    }

    public isLoading(): boolean {
        return this._loaderCounter > 0;
    }
    // #endregion Loader

    /**
     * zobrazi hlasky po vykonane operaci
     * @param messages seznam hlasek
     */
    public async showResultMessages(messages: readonly IResultMessage[] | null | undefined): Promise<void> {
        if (messages && messages.length > 0)
            await MessageWindow.showAlert(new UserMsgsBuilder([new UserMsgsData(messages)]));
    }

    private _idCounter = 0;

    public generateElementID(): string {
        return `c_${++this._idCounter}`;
    }

    public addUIHistory(state: UIHistoryState, title: string): void {
        history.pushState(state, title);
    }

    public applyApplicationTheme(theme: ApplicationTheme): void {
        this._applicationTheme = theme;

        const themeClassName = `theme-${theme}`; // `
        document.documentElement.classList.add(themeClassName);
        document.documentElement.dataset.theme = themeClassName;

        const allThemes = EnumUtils.getEnumAsArray(ApplicationTheme);
        const otherThemes = allThemes.filter(themeItem => themeItem !== theme);
        const classNamesToRemove = otherThemes.map(item => `theme-${item}`);

        document.documentElement.classList.remove(...classNamesToRemove); // `
        const iframes = document.getElementsByTagName("iframe");

        for (const iframe of iframes) {
            try {
                iframe.contentDocument.documentElement.classList.add(themeClassName);
                iframe.contentDocument.documentElement.classList.remove(...classNamesToRemove);
            } catch (e) {
                logger.groupCollapsed(`Unable to set theme class to iframe src="${iframe.src}"!`); // `
                logger.error(e.toString());
                logger.groupEnd();
            }
        }
    }

    //TODO: Někdy dodělat globální volání
    /*public dispose() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }*/
}
